import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import sessionService from '@/services/sessionService.ts';
import notificationService from '@/services/notificationService.ts';
import type { SimulationReport as Report, KpiScore } from '@/types/evaluation.ts';
import { buildVendorProfileFromKpis } from '@/utils/vendorProfileBuilder.ts';
import { maestros, getMaestroName } from '@/types/maestro.ts';
import { useAsyncData } from '@/hooks/useAsyncData.ts';
import { useReportGenerationPoller } from '@/hooks/useReportGenerationPoller.ts';
import SimulationReport from './SimulationReport.tsx';

interface SessionDetailProps {
  sessionId: string;
  onBack: () => void;
  backLabel?: string;
  /** Pitch flow: start another attempt of the same simulation (deliberate-practice loop). */
  onNewAttempt?: () => void;
  /** Pitch flow: previous attempt's overall score (0-10) for the improvement delta. */
  previousScore?: number | null;
  /** Reports the current attempt's score upward so the next retry can compare. */
  onScoreLoaded?: (score: number) => void;
}

export default function SessionDetail({ sessionId, onBack, backLabel, onNewAttempt, previousScore, onScoreLoaded }: SessionDetailProps) {
  const { t } = useTranslation();
  const resolvedBackLabel = backLabel ?? t('session.backToProgress');
  const { data: initialSession, loading, error } = useAsyncData(
    () => sessionService.getById(sessionId),
    [sessionId],
  );
  const { status, session, setOptimisticStatus } = useReportGenerationPoller(
    sessionId,
    initialSession ?? null,
  );
  const [showMessages, setShowMessages] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Surface the completed score upward (for the retry-vs-previous comparison).
  useEffect(() => {
    if (status === 'Complete' && session?.report && onScoreLoaded) {
      onScoreLoaded(session.report.overallScore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, session?.report?.overallScore]);

  useEffect(() => {
    if (!sessionId) return;
    if (status !== 'Complete' && status !== 'Failed') return;

    let cancelled = false;
    (async () => {
      try {
        const notifications = await notificationService.getAll();
        const target = notifications.find(
          (n) =>
            !n.isRead &&
            n.relatedEntityId === sessionId &&
            (n.type === 'report_ready' || n.type === 'report_failed'),
        );
        if (target && !cancelled) {
          await notificationService.markAsRead(target.id);
        }
      } catch {
        // Silent: badge cleanup is best-effort.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, status]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={onBack} className="mb-6 gap-1.5 text-muted-foreground">
          <ArrowLeft className="size-4" />
          {resolvedBackLabel}
        </Button>
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <p className="text-sm text-red-500">{error ?? t('session.loadError')}</p>
        </div>
      </div>
    );
  }

  const maestro = maestros.find((m) => m.id === session.maestroId);
  const maestroName = maestro ? getMaestroName(maestro) : session.maestroId;
  const maestroColor = maestro?.accentColor ?? 'var(--primary)';
  const durationMin = session.durationSeconds
    ? `${Math.round(session.durationSeconds / 60)} min`
    : '-';

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await sessionService.regenerateReport(sessionId);
      setOptimisticStatus('Pending');
    } catch {
      // Silent inline; the poller will resync from backend on the next tick.
    } finally {
      setRetrying(false);
    }
  };

  let report: Report | undefined;
  if (status === 'Complete' && session.report) {
    let kpis: KpiScore[] = [];
    let strengths: string[] = [];
    let improvements: string[] = [];
    let summary = '';

    try { kpis = JSON.parse(session.report.kpiScoresJson); } catch { /* ignore */ }
    try { strengths = JSON.parse(session.report.strengthsJson); } catch { /* ignore */ }
    try { improvements = JSON.parse(session.report.improvementsJson); } catch { /* ignore */ }
    try { summary = JSON.parse(session.report.summaryJson); } catch { summary = session.report.summaryJson; }

    const vendorProfile = buildVendorProfileFromKpis(kpis);

    report = {
      globalScore: session.report.overallScore,
      duration: durationMin,
      kpis,
      strengths,
      improvements,
      summary,
      improvedPitch: session.report.improvedPitch ?? undefined,
      vendorProfile,
    };
  }

  const effectiveStatus = (status ?? 'Pending') as 'Pending' | 'Processing' | 'Complete' | 'Failed';

  return (
    <div>
      <SimulationReport
        aiAnalysisStatus={effectiveStatus}
        report={report}
        maestroName={maestroName}
        maestroColor={maestroColor}
        duration={durationMin}
        onBack={onBack}
        backLabel={resolvedBackLabel}
        sessionDate={session.completedAt}
        onRetry={effectiveStatus === 'Failed' ? handleRetry : undefined}
        retrying={retrying}
        onNewAttempt={effectiveStatus === 'Complete' ? onNewAttempt : undefined}
        previousScore={previousScore}
      />

      {effectiveStatus === 'Complete' && session.messages.length > 0 && (
        <div className="mt-6 rounded-xl border border-border bg-card p-4 sm:p-6">
          <button
            onClick={() => setShowMessages(!showMessages)}
            className="flex w-full items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold text-foreground">
                {t('session.conversationTitle', { count: session.messages.length })}
              </h2>
            </div>
            {showMessages ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>

          {showMessages && (
            <div className="mt-4 space-y-3">
              {session.messages
                .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
                .map((msg) => (
                  <div
                    key={msg.id}
                    className={`rounded-lg p-3 text-sm ${
                      msg.role === 'Assistant'
                        ? 'bg-muted text-muted-foreground'
                        : 'bg-primary/10 text-foreground'
                    }`}
                  >
                    <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {msg.role === 'Assistant' ? maestroName : t('common.you')}
                    </p>
                    <p className="leading-relaxed">{msg.content}</p>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
